import React, { useState, useEffect } from 'react';
import { ThumbsUp, MessageSquare, Send, ArrowLeft, User, Clock, Pin, Lock } from 'lucide-react';
import { forumAPI, authAPI } from '../services/api';

const ThreadDetail = ({ slug, onBack }) => {
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [showThreadReply, setShowThreadReply] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [userAccount, setUserAccount] = useState(null);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [hoveredPost, setHoveredPost] = useState(null);

  useEffect(() => {
    loadThread();
    const token = localStorage.getItem('authToken');
    if (token) {
      authAPI.getCurrentUser().then(r => r.success && setUserAccount(r.account)).catch(() => {});
    }
  }, [slug]);

  useEffect(() => { if (thread?._id) loadPosts(); }, [thread?._id]);

  useEffect(() => {
    if (!thread) return undefined;

    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const canonical = document.querySelector('link[rel="canonical"]');

    const previousDescription = descriptionMeta?.getAttribute('content') || '';
    const previousOgTitle = ogTitle?.getAttribute('content') || '';
    const previousOgDescription = ogDescription?.getAttribute('content') || '';
    const previousOgUrl = ogUrl?.getAttribute('content') || '';
    const previousTwitterTitle = twitterTitle?.getAttribute('content') || '';
    const previousTwitterDescription = twitterDescription?.getAttribute('content') || '';
    const previousCanonical = canonical?.getAttribute('href') || '';

    const snippet = [thread.content, thread.linkedContent?.title && `Linked to ${thread.linkedContent.title}`]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 155);
    const threadUrl = `https://reactorhub.net/forum/${thread.slug}`;
    const title = `${thread.title} | ReactorHub Forum`;

    document.title = title;
    if (descriptionMeta) descriptionMeta.setAttribute('content', snippet);
    if (ogTitle) ogTitle.setAttribute('content', title);
    if (ogDescription) ogDescription.setAttribute('content', snippet);
    if (ogUrl) ogUrl.setAttribute('content', threadUrl);
    if (twitterTitle) twitterTitle.setAttribute('content', title);
    if (twitterDescription) twitterDescription.setAttribute('content', snippet);
    if (canonical) canonical.setAttribute('href', threadUrl);

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
      if (ogTitle) ogTitle.setAttribute('content', previousOgTitle);
      if (ogDescription) ogDescription.setAttribute('content', previousOgDescription);
      if (ogUrl) ogUrl.setAttribute('content', previousOgUrl);
      if (twitterTitle) twitterTitle.setAttribute('content', previousTwitterTitle);
      if (twitterDescription) twitterDescription.setAttribute('content', previousTwitterDescription);
      if (canonical) canonical.setAttribute('href', previousCanonical);
    };
  }, [thread]);

  const loadThread = async () => {
    try {
      const response = await forumAPI.getThread(slug);
      if (response.success) setThread(response.thread);
    } catch (error) { console.error('Error loading thread:', error); }
  };

  const loadPosts = async () => {
    if (!thread?._id) return;
    try {
      setLoading(true);
      const response = await forumAPI.getPosts(thread._id, { page: 1, limit: 50, sortBy: 'createdAt', order: 'asc' });
      setPosts(response.posts || []);
    } catch (error) { console.error('Error loading posts:', error); }
    finally { setLoading(false); }
  };

  const handleLikeThread = async () => {
    if (!userAccount) return alert('Please sign in to like threads');
    try { await forumAPI.likeThread(thread._id); loadThread(); } catch (e) { console.error(e); }
  };

  const handleLikePost = async (postId) => {
    if (!userAccount) return alert('Please sign in to like posts');
    try {
      await forumAPI.likePost(postId);
      setLikedPosts(prev => { const s = new Set(prev); s.has(postId) ? s.delete(postId) : s.add(postId); return s; });
      loadPosts();
    } catch (e) { console.error(e); }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !userAccount) return !userAccount && alert('Please sign in to post replies');
    try {
      setSubmitting(true);
      const response = await forumAPI.createPost(thread._id, { content: replyText, replyToPostId: replyingTo?._id || null });
      if (response.success && response.post) setPosts([...posts, response.post]);
      setReplyText(''); setReplyingTo(null); setShowThreadReply(false);
      loadPosts(); loadThread();
    } catch (e) { console.error(e); alert('Failed to post reply'); }
    finally { setSubmitting(false); }
  };

  const formatDate = (date) => {
    const diffMs = Date.now() - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!thread) {
    return (
      <div style={{ width: '100%', height: '100%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#6B7280', fontSize: '14px' }}>Loading thread...</div>
      </div>
    );
  }

  const ReplyForm = ({ target, onCancel }) => (
    <div style={{
      padding: '16px', background: 'rgba(139, 92, 246, 0.05)',
      borderRadius: '12px', border: '1px solid rgba(139, 92, 246, 0.2)', marginTop: '12px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
          {target ? <>Replying to <span style={{ color: '#8B5CF6', fontWeight: '600' }}>@{target.authorUsername}</span></> : '💬 Reply to thread'}
        </span>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '18px' }}>×</button>
      </div>
      <form onSubmit={handleSubmitReply}>
        <textarea
          autoFocus value={replyText} onChange={(e) => setReplyText(e.target.value)}
          placeholder="Write your reply..." disabled={!userAccount || submitting}
          style={{
            width: '100%', minHeight: '100px', padding: '12px', borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)',
            color: '#FFFFFF', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical',
            outline: 'none', marginBottom: '12px', boxSizing: 'border-box'
          }}
        />
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent', color: '#9CA3AF', fontSize: '12px', fontWeight: '500', cursor: 'pointer'
          }}>Cancel</button>
          <button type="submit" disabled={!replyText.trim() || !userAccount || submitting} style={{
            padding: '8px 20px', borderRadius: '8px', border: 'none',
            background: (!replyText.trim() || submitting) ? '#374151' : 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
            color: '#FFFFFF', fontSize: '12px', fontWeight: '600',
            cursor: (!replyText.trim() || submitting) ? 'not-allowed' : 'pointer'
          }}>{submitting ? 'Posting...' : 'Post Reply'}</button>
        </div>
      </form>
    </div>
  );

  return (
    <div style={{ width: '100%', height: '100%', background: '#000', padding: '20px', overflowY: 'auto' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Back Button */}
        <button onClick={() => onBack && onBack()} style={{
          display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', marginBottom: '16px',
          borderRadius: '8px', border: 'none', background: 'transparent', color: '#6B7280',
          fontSize: '13px', cursor: 'pointer'
        }}>
          <ArrowLeft size={14} /> Back to Forum
        </button>

        {/* Thread Card */}
        <div style={{
          background: 'rgba(255,255,255,0.02)', borderRadius: '14px',
          border: '1px solid rgba(255,255,255,0.06)', padding: '20px', marginBottom: '16px'
        }}>
          {/* Badges */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
            {thread.isPinned && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(234, 179, 8, 0.15)', color: '#EAB308', fontSize: '10px', fontWeight: '600' }}>
                <Pin size={10} /> PINNED
              </span>
            )}
            {thread.isLocked && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(107, 114, 128, 0.2)', color: '#9CA3AF', fontSize: '10px', fontWeight: '600' }}>
                <Lock size={10} /> LOCKED
              </span>
            )}
            <span style={{ padding: '3px 8px', borderRadius: '6px', background: 'rgba(139, 92, 246, 0.15)', color: '#8B5CF6', fontSize: '10px', fontWeight: '600', textTransform: 'capitalize' }}>
              {thread.category?.replace('_', ' ')}
            </span>
          </div>

          {/* Title */}
          <h1 style={{ color: '#FFFFFF', fontSize: '20px', fontWeight: '700', margin: '0 0 12px 0', lineHeight: '1.3' }}>
            {thread.title}
          </h1>

          {/* Linked Content Preview */}
          {(thread.linkedContent?.thumbnail || thread.linkedContent?.posterUrl) && (
            <div
              onClick={() => {
                const lc = thread.linkedContent;
                let url;
                if (lc.type === 'peak_moment') {
                  const vid = lc.peakMomentData?.videoId;
                  const ts = lc.peakMomentData?.timestamp;
                  url = `https://www.youtube.com/watch?v=${vid}${ts ? `&t=${ts}s` : ''}`;
                } else if (lc.type === 'channel') {
                  url = `https://www.youtube.com/watch?v=${lc.id}`;
                } else {
                  url = null; // movie/show — no reliable external link
                }
                if (url) window.open(url, '_blank');
              }}
              style={{
                display: 'flex', gap: '12px', padding: '12px', marginBottom: '14px',
                background: 'rgba(255,255,255,0.03)', borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.08)',
                cursor: thread.linkedContent.type === 'peak_moment' || thread.linkedContent.type === 'channel' ? 'pointer' : 'default'
              }}
            >
              <img
                src={thread.linkedContent.thumbnail || thread.linkedContent.posterUrl} alt=""
                style={{
                  width: thread.linkedContent.posterUrl && !thread.linkedContent.thumbnail ? '50px' : '100px',
                  height: thread.linkedContent.posterUrl && !thread.linkedContent.thumbnail ? '75px' : '56px',
                  borderRadius: '6px', objectFit: 'cover', flexShrink: 0
                }}
              />
              <div>
                <div style={{ fontSize: '10px', color: '#8B5CF6', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                  {thread.linkedContent.type === 'peak_moment' ? '🔥 Peak Moment' : thread.linkedContent.type === 'channel' ? '📹 Reactor Video' : '🎬 Movie/Show'}
                </div>
                <div style={{ fontSize: '13px', color: '#FFFFFF', fontWeight: '500' }}>{thread.linkedContent.title}</div>
                {thread.linkedContent.channelName && <div style={{ fontSize: '11px', color: '#6B7280' }}>{thread.linkedContent.channelName}</div>}
              </div>
            </div>
          )}

          {/* Author Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%', background: '#8B5CF6',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', color: '#FFFFFF'
            }}>{thread.authorUsername?.[0]?.toUpperCase()}</div>
            <div>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#FFFFFF' }}>{thread.authorUsername}</span>
              <span style={{ fontSize: '11px', color: '#6B7280', marginLeft: '8px' }}>{formatDate(thread.createdAt)}</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto', fontSize: '11px', color: '#6B7280' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MessageSquare size={11} /> {thread.stats?.replies || 0}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><ThumbsUp size={11} /> {thread.stats?.likes || 0}</span>
            </div>
          </div>

          {/* Content */}
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#D1D5DB', whiteSpace: 'pre-wrap', marginBottom: '14px' }}>
            {thread.content}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={handleLikeThread} style={{
              padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
              background: thread.likedBy?.includes(userAccount?._id) ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
              color: thread.likedBy?.includes(userAccount?._id) ? '#8B5CF6' : '#9CA3AF',
              fontSize: '12px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'
            }}><ThumbsUp size={13} /> Like</button>
            <button onClick={() => { setShowThreadReply(true); setReplyingTo(null); }} style={{
              padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
              background: showThreadReply ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
              color: showThreadReply ? '#8B5CF6' : '#9CA3AF',
              fontSize: '12px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px'
            }}><MessageSquare size={13} /> Reply</button>
          </div>

          {/* Thread Reply Form */}
          {showThreadReply && <ReplyForm target={null} onCancel={() => { setShowThreadReply(false); setReplyText(''); }} />}
        </div>

        {/* Replies Section */}
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#9CA3AF' }}>
            {posts.length} {posts.length === 1 ? 'Reply' : 'Replies'}
          </h2>

          {loading ? (
            <div style={{ color: '#6B7280', textAlign: 'center', padding: '30px', fontSize: '13px' }}>Loading replies...</div>
          ) : posts.length === 0 ? (
            <div style={{
              background: 'rgba(255,255,255,0.02)', borderRadius: '12px', padding: '30px',
              textAlign: 'center', border: '1px dashed rgba(255,255,255,0.1)'
            }}>
              <MessageSquare size={24} color="#6B7280" style={{ marginBottom: '8px', opacity: 0.5 }} />
              <p style={{ color: '#6B7280', margin: 0, fontSize: '13px' }}>No replies yet. Be the first to comment!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {posts.map((post, index) => {
                const isReply = !!post.replyToPostId;
                const isHovered = hoveredPost === index;

                return (
                  <div key={post._id || index}>
                    <div
                      onMouseEnter={() => setHoveredPost(index)}
                      onMouseLeave={() => setHoveredPost(null)}
                      style={{
                        paddingLeft: isReply ? '32px' : '0', padding: '12px',
                        background: isHovered ? 'rgba(255,255,255,0.02)' : 'transparent',
                        borderRadius: '10px', borderLeft: isReply ? '2px solid #4B5563' : 'none',
                        marginLeft: isReply ? '16px' : '0'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '50%', background: '#8B5CF6',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '11px', fontWeight: '600', color: '#FFFFFF', flexShrink: 0
                        }}>{post.authorUsername?.[0]?.toUpperCase()}</div>

                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#FFFFFF' }}>{post.authorUsername}</span>
                            {post.authorRole === 'admin' && (
                              <span style={{ marginLeft: '6px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(249, 115, 22, 0.2)', color: '#F97316', fontSize: '9px', fontWeight: '700' }}>ADMIN</span>
                            )}
                            <span style={{ fontSize: '11px', color: '#6B7280', marginLeft: '8px' }}>{formatDate(post.createdAt)}</span>
                          </div>

                          {post.replyToUsername && (
                            <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>
                              <span style={{ color: '#8B5CF6' }}>@{post.replyToUsername}</span>
                            </div>
                          )}

                          <div style={{ fontSize: '13px', lineHeight: '1.5', color: '#D1D5DB', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
                            {post.content}
                          </div>

                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <button onClick={() => handleLikePost(post._id)} style={{
                              padding: '3px 8px', borderRadius: '6px', border: 'none',
                              background: likedPosts.has(post._id) ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                              color: likedPosts.has(post._id) ? '#8B5CF6' : '#6B7280',
                              fontSize: '11px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                            }}><ThumbsUp size={11} /> {post.stats?.likes || 0}</button>
                            <button onClick={() => { setReplyingTo(post); setShowThreadReply(false); }} style={{
                              padding: '3px 8px', borderRadius: '6px', border: 'none', background: 'transparent',
                              color: '#6B7280', fontSize: '11px', fontWeight: '500', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '4px'
                            }}><MessageSquare size={11} /> Reply</button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {replyingTo && replyingTo._id === post._id && (
                      <div style={{ marginLeft: isReply ? '48px' : '40px' }}>
                        <ReplyForm target={post} onCancel={() => { setReplyingTo(null); setReplyText(''); }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom Reply Box */}
        {!thread.isLocked && !replyingTo && !showThreadReply && (
          <div style={{
            background: 'rgba(255,255,255,0.02)', borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.06)', padding: '20px'
          }}>
            <h3 style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '600', marginBottom: '12px', marginTop: 0 }}>
              Join the Discussion
            </h3>
            <form onSubmit={handleSubmitReply}>
              <textarea
                value={replyText} onChange={(e) => setReplyText(e.target.value)}
                placeholder={userAccount ? "Share your thoughts..." : "Sign in to join the discussion"}
                disabled={!userAccount}
                style={{
                  width: '100%', minHeight: '100px', padding: '12px', borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)',
                  color: '#FFFFFF', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical',
                  outline: 'none', marginBottom: '12px', boxSizing: 'border-box'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" disabled={!replyText.trim() || submitting || !userAccount} style={{
                  padding: '10px 20px', borderRadius: '10px', border: 'none',
                  background: (!replyText.trim() || !userAccount) ? '#374151' : 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                  color: '#FFFFFF', fontSize: '13px', fontWeight: '600',
                  cursor: (!replyText.trim() || !userAccount) ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}><Send size={14} /> {submitting ? 'Posting...' : 'Post Reply'}</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadDetail;
